import { env } from '@/shared/config/env'
import type { HttpGet, HttpPost, HttpPostMultipart, HttpRequest } from './http-client'
import { HttpError } from './http-client'

const CLIENT_PLATFORM = 'cc_web'

export class AnonEdgeHttpClient implements HttpGet, HttpPost, HttpPostMultipart {
  constructor(
    private readonly baseUrl: string,
    private readonly anonKey: string,
  ) {}

  async get<R>(request: HttpRequest): Promise<R> {
    const response = await fetch(this.buildUrl(request.url), {
      method: 'GET',
      headers: this.buildHeaders(),
      signal: request.signal,
      cache: 'no-store',
    })
    return this.handleResponse<R>(response, request.url, 'GET')
  }

  async post<B, R>(request: HttpRequest<B>): Promise<R> {
    const response = await fetch(this.buildUrl(request.url), {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(request.body),
    })
    return this.handleResponse<R>(response, request.url, 'POST')
  }

  async postMultipart<R>(req: {
    url: string
    form: FormData
    headers?: Record<string, string>
  }): Promise<R> {
    const response = await fetch(this.buildUrl(req.url), {
      method: 'POST',
      headers: {
        apikey: this.anonKey,
        Authorization: `Bearer ${this.anonKey}`,
        ...this.platformHeaders(),
        ...req.headers,
      },
      body: req.form,
    })
    return this.handleResponse<R>(response, req.url, 'POST')
  }

  private buildUrl(url: string): string {
    return `${this.baseUrl.replace(/\/$/, '')}${url}`
  }

  private platformHeaders(): Record<string, string> {
    return {
      'X-BB-Client': CLIENT_PLATFORM,
      'X-BB-App-Version': env.appVersion,
    }
  }

  private buildHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      apikey: this.anonKey,
      Authorization: `Bearer ${this.anonKey}`,
      ...this.platformHeaders(),
    }
  }

  private async handleResponse<R>(
    response: Response,
    requestUrl?: string,
    method?: string,
  ): Promise<R> {
    if (!response.ok) {
      throw new HttpError(
        response.status,
        await this.safeParseJson(response),
        requestUrl,
        method,
      )
    }
    if (response.status === 204) {
      return undefined as R
    }
    const body = await response.json()
    if (body !== null && typeof body === 'object' && 'ok' in body) {
      if (body.ok === false) {
        throw new Error(body.error ?? 'Request failed')
      }
      if (body.ok === true) {
        if ('data' in body) {
          return body.data as R
        }
        return undefined as R
      }
    }
    return body as R
  }

  private async safeParseJson(response: Response): Promise<unknown> {
    try {
      return await response.json()
    } catch {
      return undefined
    }
  }
}
